import { useEffect, useRef, useState } from "react";

import { useLeagueApi } from "../../../data/LeagueEnvironmentProvider";
import {
  PlayerStatsGarden,
  PlayerStatsPlayerResponse,
  PlayerStatsPopulationsResponse,
  PlayerStatsScopesResponse,
  PlayerStatsTableResponse,
} from "../../../models/PlayerStats";

/**
 * Plain hooks rather than a Zustand store — this page owns its data and shares
 * none of it, the same call the research wiki made (`useArticles.ts`).
 */

export interface AsyncState<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
}

const initial = <T,>(): AsyncState<T> => ({
  data: null,
  loading: true,
  error: null,
});

function messageFor(e: any): string {
  return e?.response?.data?.error || e?.message || "Request failed";
}

/**
 * Runs `load`, aborts whatever it supersedes, and commits only if it's still the
 * newest call.
 *
 * Both halves matter and they fix different things. The id guard stops a slow
 * earlier response landing on top of a fast later one — the grid showing the wrong
 * season. The abort stops the superseded *request* from existing at all, which is
 * what kept the spinner up forever:
 *
 * A browser allows ~6 connections per origin. Every section change is a request,
 * and a cold one is a couple of seconds of Snowflake. Switch tabs a few times
 * quickly and the abandoned requests — which nothing cancelled and axios never
 * times out — hold every slot. The newest request then queues behind them, never
 * starts, never settles, and `loading` stays true with no error to show for it.
 *
 * `ExplorerStore` solved the same problem the same way for rapid filter changes.
 */
function useLatest<T>(
  load: ((signal: AbortSignal) => Promise<T>) | null,
  deps: unknown[],
): AsyncState<T> {
  const [state, setState] = useState<AsyncState<T>>(initial<T>);
  const requestId = useRef(0);

  useEffect(() => {
    if (!load) {
      setState({ data: null, loading: false, error: null });
      return;
    }
    const id = ++requestId.current;
    const controller = new AbortController();
    setState((prev) => ({ ...prev, loading: true, error: null }));
    load(controller.signal)
      .then((data) => {
        if (id === requestId.current) {
          setState({ data, loading: false, error: null });
        }
      })
      .catch((e) => {
        // An abort is this hook's own doing, not a failure. Surfacing it would
        // flash an error between two legitimate views, and clearing `loading`
        // would hide the spinner for the request that replaced it.
        if (controller.signal.aborted) return;
        if (id === requestId.current) {
          setState({ data: null, loading: false, error: messageFor(e) });
        }
      });
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return state;
}

export function usePlayerStatsScopes(
  garden: PlayerStatsGarden,
): AsyncState<PlayerStatsScopesResponse> {
  const api = useLeagueApi();
  return useLatest(
    (signal) => api.getPlayerStatsScopes(garden, signal).then((r) => r.data),
    [api, garden],
  );
}

/**
 * Rows for one section. The endpoint serves a single section's columns, so
 * changing sub-tab refetches rather than switching client-side — see
 * `app/api/player_stats/CLAUDE.md` for why. `useLatest` already guards against a
 * slow earlier response landing on a fast later one, which matters more here now
 * that a section change is a request.
 *
 * `cols` names columns the filters need that the section doesn't carry. They're
 * already in the server's cache entry for this scope, so asking for them is
 * nearly free — but without them the row arrives with no value for the filtered
 * field and `applyFilters` drops it.
 */
export function usePlayerStatsTable(
  garden: PlayerStatsGarden,
  league: string | null,
  season: number | null,
  section: string | null,
  cols: string[] = [],
): AsyncState<PlayerStatsTableResponse> {
  const api = useLeagueApi();
  // Sorted and joined so the dependency is the *set* of columns: reordering the
  // filter rows must not refetch, and the array identity changes every render.
  const colKey = Array.from(new Set(cols)).sort().join(",");
  return useLatest(
    league && season
      ? (signal) =>
          api
            .getPlayerStatsTable(
              garden,
              league,
              season,
              section,
              colKey ? colKey.split(",") : [],
              signal,
            )
            .then((r) => r.data)
      : null,
    [api, garden, league, season, section, colKey],
  );
}

export function usePlayerStatsPlayer(
  garden: PlayerStatsGarden,
  playerId: string | undefined,
): AsyncState<PlayerStatsPlayerResponse> {
  const api = useLeagueApi();
  return useLatest(
    playerId
      ? (signal) =>
          api.getPlayerStatsPlayer(garden, playerId, signal).then((r) => r.data)
      : null,
    [api, garden, playerId],
  );
}

/**
 * League populations for the player page's scopes, so percentiles can be
 * recomputed against the players currently passing the filters.
 *
 * Only fetched when something needs it — a cold scope costs a Snowflake read, and
 * the default "vs league" mode is served entirely by the blob's own percentiles.
 * The page renders those immediately and this fills in behind, so nothing blocks
 * on it.
 */
export function usePlayerStatsPopulations(
  garden: PlayerStatsGarden,
  scopes: string[],
  section: string | null,
  cols: string[] = [],
  enabled = true,
): AsyncState<PlayerStatsPopulationsResponse> {
  const api = useLeagueApi();
  const scopeKey = Array.from(new Set(scopes)).sort().join(",");
  const colKey = Array.from(new Set(cols)).sort().join(",");
  return useLatest(
    enabled && scopeKey
      ? (signal) =>
          api
            .getPlayerStatsPopulations(
              garden,
              scopeKey.split(","),
              section,
              colKey ? colKey.split(",") : [],
              signal,
            )
            .then((r) => r.data)
      : null,
    [api, garden, scopeKey, section, colKey, enabled],
  );
}
